import { TypedDocumentNode, gql } from '@apollo/client';
import { differenceInWeeks } from 'date-fns';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ExpiresIn } from '@sorare/core/src/components/clubShop/ClubShopItem/Labels/ExpiresIn';
import { FewLeft } from '@sorare/core/src/components/clubShop/ClubShopItem/Labels/FewLeft';
import { InventoryCounter } from '@sorare/core/src/components/clubShop/ClubShopItem/Labels/InventoryCounter';
import { ItemCounter } from '@sorare/core/src/components/clubShop/ClubShopItem/Labels/ItemCounter';
import { LimitReached } from '@sorare/core/src/components/clubShop/ClubShopItem/Labels/LimitReached';
import { New } from '@sorare/core/src/components/clubShop/ClubShopItem/Labels/New';
import { ResetIn } from '@sorare/core/src/components/clubShop/ClubShopItem/Labels/ResetIn';
import { SoldOut } from '@sorare/core/src/components/clubShop/ClubShopItem/Labels/SoldOut';
import { SpecialEvent } from '@sorare/core/src/components/clubShop/ClubShopItem/Labels/SpecialEvent';
import { isType } from '@sorare/core/src/lib/gql';

import { Header_shopItem } from './__generated__/index.graphql';

const Root = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;
const Labels = styled(Vertical).attrs({ gap: 0.5 })`
  align-items: flex-start;
`;
const CounterWrapper = styled(Vertical)`
  align-items: flex-end;
`;

type Props = { item: Header_shopItem; inventory?: boolean };

export const Header = ({ item, inventory }: Props) => {
  const isNew =
    !item.seenInManagerId ||
    differenceInWeeks(new Date(), item.createdAt) === 0;
  const isSpecialEvent = !!item.requirementsRulesLink;
  const limitReached = item.myPurchasesCount === item.limitPerUser;
  const noCooldown = item.myLimitResetAt === null;

  if (isType(item, 'JerseyShopItem')) {
    if (inventory) {
      return null;
    }
    const currentStock = item.currentStockCount || 0;

    return (
      <Root>
        <Labels>
          {isNew && <New />}
          {isSpecialEvent && <SpecialEvent />}
        </Labels>
        {noCooldown ? (
          <>
            {currentStock === 0 ? (
              <SoldOut />
            ) : (
              currentStock < 20 && <FewLeft />
            )}
          </>
        ) : (
          <ResetIn myLimitResetAt={item.myLimitResetAt!} />
        )}
      </Root>
    );
  }

  if (isType(item, 'DeliverableWithNoVariantShopItem')) {
    if (inventory) {
      return null;
    }
    return (
      <Root>
        <Labels>
          {isNew && <New />}
          {isSpecialEvent && <SpecialEvent />}
        </Labels>
        {noCooldown ? (
          <>
            {item.deliverableCurrentStockCount === 0 ? (
              <SoldOut />
            ) : (
              <FewLeft />
            )}
          </>
        ) : (
          <ResetIn myLimitResetAt={item.myLimitResetAt!} />
        )}
      </Root>
    );
  }

  if (
    isType(item, 'LevelUpShopItem') ||
    isType(item, 'ExtraSwapShopItem') ||
    isType(item, 'RaffleShopItem') ||
    isType(item, 'AppearanceBoostShopItem') ||
    isType(item, 'CommonCardSwapShopItem')
  ) {
    return (
      <Root>
        {inventory ? (
          <InventoryCounter amount={item.myBalance} />
        ) : (
          <>
            <Labels>
              {isSpecialEvent && <SpecialEvent />}
              {isNew && <New />}
              {limitReached && noCooldown && <LimitReached />}
            </Labels>
            {noCooldown ? (
              <CounterWrapper gap={0}>
                {item.expirationDate && (
                  <ExpiresIn expirationDate={item.expirationDate} />
                )}
                <ItemCounter
                  count={item.myPurchasesCount}
                  limit={item.limitPerUser}
                />
              </CounterWrapper>
            ) : (
              <ResetIn myLimitResetAt={item.myLimitResetAt!} />
            )}
          </>
        )}
      </Root>
    );
  }

  return !inventory ? (
    <Root>
      <Labels>{isNew && <New />}</Labels>
    </Root>
  ) : null;
};

Header.fragments = {
  shopItem: gql`
    fragment Header_shopItem on ClubShopItem {
      ... on ShopItemInterface {
        id
        createdAt
        limitPerUser
        myLimitResetAt
        expirationDate
        myPurchasesCount
        myBalance
        requirementsRulesLink
        seenInManagerId
      }
      ... on JerseyShopItem {
        id
        currentStockCount
      }
      ... on DeliverableWithNoVariantShopItem {
        id
        deliverableCurrentStockCount: currentStockCount
      }
    }
  ` as TypedDocumentNode<Header_shopItem>,
};
